// src/utils/api-helpers.js  // frontend code
import { auth } from '../firebase/firebaseConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const initializeUserInDatabase = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('No authenticated user to initialize');
      return null;
    }

    const idToken = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/auth/initialize-user`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to initialize user: ${response.status}`);
    }

    const data = await response.json();
    console.log('User initialized in database:', data);

    // Store user data in localStorage for quick access
    localStorage.setItem('userRequestData', JSON.stringify({
      numberOfRequests: data.numberOfRequests,
      freeRequestsRemaining: data.freeRequestsRemaining,
      hasSubscription: data.hasSubscription,
      lastUpdated: new Date().toISOString()
    }));

    return data;
  } catch (error) {
    console.error('Failed to initialize user in database:', error);
    return null;
  }
};

export const getUserRequestStatus = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;

    // Try the correct endpoint first
    const idToken = await user.getIdToken();
    const response = await fetch(`${API_BASE_URL}/api/auth/user-status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // If the endpoint doesn't exist, return cached data
      const cachedData = localStorage.getItem('userRequestData');
      if (cachedData) {
        return JSON.parse(cachedData);
      }
      console.warn('User status endpoint not available, using cached data');
      return null;
    }

    const data = await response.json();

    // Update localStorage
    localStorage.setItem('userRequestData', JSON.stringify({
      numberOfRequests: data.numberOfRequests,
      freeRequestsRemaining: data.freeRequestsRemaining,
      hasSubscription: data.subscription ? true : false,
      subscription: data.subscription,
      lastUpdated: new Date().toISOString()
    }));

    return data;
  } catch (error) {
    console.warn('Could not get user request status:', error.message);
    // Return cached data if available
    const cachedData = localStorage.getItem('userRequestData');
    if (cachedData) {
      return JSON.parse(cachedData);
    }
    return null;
  }
};

export const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idToken = await user.getIdToken();
  return {
    'Authorization': `Bearer ${idToken}`,
    'Content-Type': 'application/json'
  };
};

// Helper function to parse SSE format responses
const parseSSEResponse = (responseText, provider) => {
  console.log('📡 Parsing SSE response for', provider);
  console.log('📝 Response length:', responseText.length);
  console.log('📝 First 500 chars:', responseText.substring(0, 500));

  // Split by newlines and process each line
  const lines = responseText.split('\n');
  console.log(`📝 Total lines: ${lines.length}`);

  let accumulatedContent = '';
  let processedChunks = 0;
  let debugInfo = [];

  for (const line of lines) {
    // Skip empty lines and non-data lines
    if (!line || !line.startsWith('data: ')) continue;

    // Extract the JSON part after "data: "
    const jsonStr = line.substring(6).trim();

    // Skip the [DONE] marker
    if (jsonStr === '[DONE]') {
      console.log('✅ SSE stream complete marker found');
      continue;
    }

    try {
      const parsed = JSON.parse(jsonStr);
      processedChunks++;

      // Debug: log the structure of the first chunk
      if (processedChunks === 1) {
        console.log('🔍 First chunk structure:', JSON.stringify(parsed, null, 2).substring(0, 500));
      }

      // Extract content based on provider format - check ALL possible paths
      let chunkContent = '';

      if (provider === 'deepseek') {
        // Try multiple possible paths for DeepSeek
        chunkContent = parsed.choices?.[0]?.delta?.content ||
          parsed.choices?.[0]?.message?.content ||
          parsed.choices?.[0]?.text ||
          parsed.delta?.content ||
          parsed.content ||
          parsed.text || '';

        // Special handling: if this looks like a complete message (has 'message' not 'delta')
        // This can happen when SSE is used but the response is not actually streamed
        if (!chunkContent && parsed.choices?.[0]?.message?.content) {
          console.log('🔍 Found complete message in SSE format at chunk:', processedChunks);
          chunkContent = parsed.choices[0].message.content;
        }

        // Another special case: sometimes the content is directly in choices[0]
        if (!chunkContent && parsed.choices?.[0] && typeof parsed.choices[0] === 'string') {
          console.log('🔍 Found string content directly in choices[0]');
          chunkContent = parsed.choices[0];
        }

        // Debug logging for content extraction
        if (processedChunks <= 3) {
          debugInfo.push({
            chunk: processedChunks,
            hasChoices: !!parsed.choices,
            hasDelta: !!parsed.choices?.[0]?.delta,
            hasMessage: !!parsed.choices?.[0]?.message,
            hasContent: !!chunkContent,
            contentLength: chunkContent.length
          });
        }
      } else if (provider === 'claude' || provider === 'anthropic') {
        // Claude streaming format
        chunkContent = parsed.delta?.text ||
          parsed.content?.[0]?.text ||
          parsed.content ||
          parsed.text || '';
      }

      // If still no content, try to find any text-like field (last resort)
      if (!chunkContent && processedChunks <= 5) {
        const possibleFields = ['content', 'text', 'message', 'response', 'output', 'result'];
        for (const field of possibleFields) {
          if (parsed[field] && typeof parsed[field] === 'string') {
            console.log(`🔧 Found content in field '${field}'`);
            chunkContent = parsed[field];
            break;
          }
        }

        // Also check if the entire parsed object is actually a string (rare but possible)
        if (!chunkContent && typeof parsed === 'string') {
          console.log('🔧 Parsed object is a string itself');
          chunkContent = parsed;
        }
      }

      accumulatedContent += chunkContent;

    } catch (parseError) {
      console.warn('Failed to parse SSE chunk:', jsonStr.substring(0, 100));
      console.warn('Parse error:', parseError.message);
      // Continue processing other chunks
    }
  }

  console.log('🔍 Debug info for first chunks:', debugInfo);
  console.log(`✅ Processed ${processedChunks} SSE chunks`);
  console.log(`📊 Total accumulated content: ${accumulatedContent.length} chars`);

  if (accumulatedContent.length === 0 && processedChunks > 0) {
    console.error('⚠️ WARNING: Processed chunks but no content extracted!');
    console.log('📝 Sample of raw response:', responseText.substring(0, 1000));

    // Last resort: try to find ANY content in the response
    const contentMatch = responseText.match(/"content"\s*:\s*"([^"\\]*(\\.[^"\\]*)*)"/);
    if (contentMatch) {
      console.log('🔧 Using regex fallback to extract content');
      accumulatedContent = contentMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
    }
  }

  return {
    content: accumulatedContent,
    streaming: true,
    chunks: processedChunks,
    debugInfo
  };
};

// Helper function to parse regular JSON responses
const parseJSONResponse = (responseText, provider) => {
  console.log('📦 Parsing regular JSON response for', provider);
  console.log('📝 Response length:', responseText.length);

  const parsed = JSON.parse(responseText);
  console.log('🔍 Parsed structure keys:', Object.keys(parsed));

  let content = '';

  if (provider === 'deepseek') {
    // DeepSeek non-streaming format - try multiple paths
    content = parsed.choices?.[0]?.message?.content ||
      parsed.choices?.[0]?.text ||
      parsed.message?.content ||
      parsed.content ||
      parsed.text || '';

    // Debug log
    if (!content && parsed.choices?.[0]) {
      console.log('🔍 DeepSeek choice[0] keys:', Object.keys(parsed.choices[0]));
      if (parsed.choices[0].message) {
        console.log('🔍 Message keys:', Object.keys(parsed.choices[0].message));
      }
    }
  } else if (provider === 'claude' || provider === 'anthropic') {
    // Claude non-streaming format
    content = parsed.content?.[0]?.text ||
      parsed.content ||
      parsed.text || '';
  }

  console.log(`📊 Extracted content length: ${content.length} chars`);

  return {
    content: content,
    streaming: false,
    raw: parsed
  };
};

// Updated callLLMProxy with SSE support (non-streaming version) - FIXED ABORT HANDLING
export const callLLMProxy = async (provider, data, signal = null) => {
  let controller;
  let timeoutId;
  let cleanupAbortListener;

  try {
    const headers = await getAuthHeaders();

    // Ensure temperature is a valid float
    if (data.temperature !== undefined) {
      data.temperature = parseFloat(data.temperature) || 0.7;
      // Clamp temperature to valid range (0-2 for most providers)
      data.temperature = Math.min(2.0, Math.max(0.0, data.temperature));
    }

    let endpoint;
    const normalizedProvider = provider.toLowerCase();
    switch (normalizedProvider) {
      case 'deepseek':
        endpoint = '/api/llm/deepseek';
        break;
      case 'anthropic':
      case 'claude':
        endpoint = '/api/llm/claude';
        break;
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }

    console.log(`🚀 Calling ${provider} at ${endpoint}`);

    // Create a local abort controller that combines with passed signal
    controller = new AbortController();

    // If an external signal is provided, link it
    if (signal) {
      if (signal.aborted) {
        // Already aborted
        console.log(`🛑 ${provider} request already aborted`);
        throw new Error(`${provider} request was cancelled`);
      }

      // Listen for abort event
      const abortHandler = () => {
        console.log(`🛑 Aborting ${provider} request due to external signal`);
        // Don't abort immediately, just mark for cleanup
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
      };

      // Define cleanup function
      cleanupAbortListener = () => {
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
      };

      signal.addEventListener('abort', abortHandler);
    }

    // Timeout handling
    timeoutId = setTimeout(() => {
      console.log('⏱️ Request timeout - aborting...');
      if (controller && !controller.signal.aborted) {
        controller.abort();
      }
    }, 180000); // 3 minutes

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      signal: controller.signal  // Use the controller's signal
    });

    clearTimeout(timeoutId);
    timeoutId = null;

    if (!response.ok) {
      let errorMessage;
      try {
        const error = await response.json();
        errorMessage = error.error || error.message;
      } catch {
        errorMessage = `HTTP ${response.status} error`;
      }

      console.error(`❌ LLM API error:`, errorMessage);

      // Provide more specific error messages
      if (response.status === 504) {
        throw new Error('Request timeout - the AI is taking too long. Try with a shorter prompt.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      } else if (response.status === 503) {
        throw new Error(`${provider} service is not available. Please try another provider.`);
      } else {
        throw new Error(errorMessage || 'LLM API call failed');
      }
    }

    // Wrap response text reading in try-catch to handle abort during read
    let responseText;
    try {
      responseText = await response.text();
    } catch (readError) {
      if (readError.name === 'AbortError') {
        console.log(`🛑 Response reading aborted for ${provider}`);
        throw new Error(`${provider} request was cancelled`);
      }
      throw readError;
    }

    // Debug: Log first 200 chars to see format
    console.log('📝 Response preview:', responseText.substring(0, 200));

    let result;

    // Check if response is SSE format (starts with "data: ")
    if (responseText.startsWith('data: ')) {
      console.log('🔄 Detected SSE (streaming) response format');
      result = parseSSEResponse(responseText, normalizedProvider);
    } else {
      // Regular JSON response
      console.log('📦 Detected regular JSON response format');
      try {
        result = parseJSONResponse(responseText, normalizedProvider);
      } catch (parseError) {
        console.error('❌ Failed to parse response:', parseError);
        console.error('Response text:', responseText.substring(0, 500));
        throw new Error(`Invalid response format from ${provider}: ${parseError.message}`);
      }
    }

    // Ensure we have content
    if (!result.content && result.chunks > 0) {
      // If we processed chunks but got no content, there might be a format issue
      console.error('⚠️ Processed SSE chunks but no content extracted');
      console.error('🔍 Result object:', JSON.stringify(result, null, 2));
      console.error('📝 Response preview (first 800 chars):', responseText.substring(0, 800));

      // Try to extract any text from the response as fallback
      const fallbackMatch = responseText.match(/"content"\s*:\s*"([^"\\]*(\\.[^"\\]*)*)"/);
      if (fallbackMatch) {
        console.log('🔧 Using fallback regex content extraction');
        result.content = fallbackMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      }
    }

    if (!result.content) {
      console.warn('⚠️ Empty content in response');
      console.warn('🔍 Full result object:', JSON.stringify(result, null, 2));
      console.warn('📝 Full response text (first 1500 chars):', responseText.substring(0, 1500));

      // Check if maybe the entire response is an error message
      if (responseText.includes('error') || responseText.includes('Error')) {
        const errorInfo = responseText.match(/"error"\s*:\s*"([^"]+)"/);
        if (errorInfo) {
          throw new Error(`API returned error: ${errorInfo[1]}`);
        }
      }

      throw new Error(`No content in response from ${provider} - received ${responseText.length} chars, ${result.chunks || 0} chunks processed`);
    }

    console.log(`✅ ${provider} response received - ${result.content.length} chars, streaming: ${result.streaming}`);
    return result;

  } catch (error) {
    // Handle abort errors specifically
    if (error.name === 'AbortError' || 
        (error.message && error.message.includes('was cancelled')) ||
        (error.message && error.message.includes('aborted'))) {
      console.log(`⏱️ ${provider} request aborted`);
      // Don't re-throw AbortError, throw a clean cancellation error
      throw new Error(`${provider} request was cancelled`);
    }
    console.error(`❌ Error calling ${provider}:`, error);
    throw error;
  } finally {
    // Cleanup
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (cleanupAbortListener) {
      cleanupAbortListener();
    }
  }
};

// ==================== ALSO UPDATE callLLMProxyStreaming IN api-helpers.js ====================
// In api-helpers.js, update the callLLMProxyStreaming function to better handle errors:

export const callLLMProxyStreaming = async (provider, data, signal = null, onChunk = null) => {
  let controller;
  let timeoutId;
  let cleanupAbortListener;
  let reader;

  try {
    const headers = await getAuthHeaders();

    // Ensure temperature is valid
    if (data.temperature !== undefined) {
      data.temperature = parseFloat(data.temperature) || 0.7;
      data.temperature = Math.min(2.0, Math.max(0.0, data.temperature));
    }

    // Add streaming flag to request
    data.stream = true;

    let endpoint;
    const normalizedProvider = provider.toLowerCase();
    switch (normalizedProvider) {
      case 'deepseek':
        endpoint = '/api/llm/deepseek';
        break;
      case 'anthropic':
      case 'claude':
        endpoint = '/api/llm/claude';
        break;
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }

    console.log(`🚀 Calling ${provider} at ${endpoint} with streaming`);
    console.log('📦 Request data:', {
      model: data.model,
      temperature: data.temperature,
      stream: data.stream,
      promptLength: data.prompt?.length || data.messages?.[0]?.content?.length || 0
    });

    controller = new AbortController();

    if (signal) {
      if (signal.aborted) {
        throw new Error(`${provider} request was cancelled`);
      }

      const abortHandler = () => {
        console.log(`🛑 Aborting ${provider} streaming request due to external signal`);
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
        // Cancel the reader if it exists
        if (reader) {
          reader.cancel().catch(() => {}); // Ignore errors from cancelling
        }
      };

      cleanupAbortListener = () => {
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
      };

      signal.addEventListener('abort', abortHandler);
    }

    timeoutId = setTimeout(() => {
      console.log('⏱️ Streaming request timeout - aborting...');
      if (controller && !controller.signal.aborted) {
        controller.abort();
      }
    }, 180000); // 3 minutes

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    timeoutId = null;

    // Check content type to see if it's actually streaming
    const contentType = response.headers.get('content-type');
    console.log(`📋 Response content-type: ${contentType}`);
    console.log(`📋 Response status: ${response.status}`);

    if (!response.ok) {
      let errorMessage;
      try {
        const errorText = await response.text();
        console.error(`❌ Error response text:`, errorText);

        // Try to parse as JSON
        try {
          const error = JSON.parse(errorText);
          errorMessage = error.error || error.message || errorText;
        } catch {
          errorMessage = errorText || `HTTP ${response.status} error`;
        }
      } catch {
        errorMessage = `HTTP ${response.status} error`;
      }

      console.error(`❌ LLM Streaming API error:`, errorMessage);

      if (response.status === 504) {
        throw new Error('Request timeout - the AI is taking too long. Try with a shorter prompt.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      } else if (response.status === 503) {
        throw new Error(`${provider} service is not available. Please try another provider.`);
      } else {
        throw new Error(errorMessage || 'LLM API call failed');
      }
    }

    // Check if response is actually streaming (SSE)
    const isStreaming = contentType && (
      contentType.includes('text/event-stream') ||
      contentType.includes('application/x-ndjson') ||
      contentType.includes('text/plain') // Some APIs use text/plain for streaming
    );

    if (!isStreaming) {
      console.warn(`⚠️ Response is not streaming format (content-type: ${contentType}). Parsing as regular JSON.`);

      // Try to parse as regular JSON response
      let responseText;
      try {
        responseText = await response.text();
      } catch (readError) {
        if (readError.name === 'AbortError') {
          throw new Error(`${provider} request was cancelled`);
        }
        throw readError;
      }

      try {
        const parsed = JSON.parse(responseText);

        // Extract content based on provider
        let content = '';
        if (normalizedProvider === 'deepseek') {
          content = parsed.choices?.[0]?.message?.content ||
            parsed.choices?.[0]?.text ||
            parsed.content || '';
        } else if (normalizedProvider === 'gemini') {
          content = parsed.candidates?.[0]?.content?.parts?.[0]?.text ||
            parsed.text ||
            parsed.content || '';
        } else if (normalizedProvider === 'claude') {
          content = parsed.content?.[0]?.text ||
            parsed.content ||
            parsed.text || '';
        }

        if (content) {
          console.log(`✅ Got non-streaming response with ${content.length} chars`);

          // Call onChunk to simulate streaming if provided
          if (onChunk) {
            onChunk(content, content);
          }

          return content;
        } else {
          console.error('❌ No content found in non-streaming response:', parsed);
          throw new Error('No content in response');
        }
      } catch (e) {
        console.error('❌ Failed to parse non-streaming response:', e);
        throw new Error('Invalid response format');
      }
    }

    // Process streaming response
    reader = response.body?.getReader();
    const decoder = new TextDecoder('utf-8', { stream: true });
    let accumulatedContent = '';
    let chunkCount = 0;
    let buffer = ''; // Buffer for incomplete chunks

    if (reader) {
      console.log('[DEBUG] Starting to read stream');

      try {
        while (true) {
          if (signal?.aborted) {
            reader.cancel().catch(() => {}); // Ignore errors
            throw new Error('Streaming aborted by user');
          }

          const { done, value } = await reader.read();
          if (done) {
            console.log('[DEBUG] Stream reading complete');
            break;
          }

          chunkCount++;
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk; // Add to buffer

          // Process complete lines from buffer
          const lines = buffer.split('\n');
          // Keep the last potentially incomplete line in the buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line || !line.trim()) continue;

            // Handle SSE format
            if (line.startsWith('data: ')) {
              const jsonStr = line.substring(6).trim();
              if (jsonStr === '[DONE]') continue;

              try {
                const parsed = JSON.parse(jsonStr);
                let chunkContent = '';

                // Extract content based on provider
                if (normalizedProvider === 'deepseek') {
                  chunkContent = parsed.choices?.[0]?.delta?.content ||
                    parsed.choices?.[0]?.message?.content || '';
                } else if (normalizedProvider === 'gemini' || normalizedProvider === 'google gemini') {
                  chunkContent = parsed.candidates?.[0]?.content?.parts?.[0]?.text || '';
                } else if (normalizedProvider === 'claude' || normalizedProvider === 'anthropic') {
                  chunkContent = parsed.delta?.text ||
                    parsed.content?.[0]?.text || '';
                }

                if (chunkContent) {
                  accumulatedContent += chunkContent;
                  // Call the chunk callback if provided
                  if (onChunk) {
                    onChunk(chunkContent, accumulatedContent);
                  }
                }
              } catch (e) {
                console.warn('Failed to parse streaming chunk:', e.message);
                console.warn('Problematic line:', line);
              }
            } else {
              // Handle non-SSE streaming (raw text chunks)
              accumulatedContent += line;
              if (onChunk) {
                onChunk(line, accumulatedContent);
              }
            }
          }

          // Small delay to prevent UI blocking
          if (chunkCount % 10 === 0) {
            await new Promise(resolve => setTimeout(resolve, 10));
          }
        }

        // Process any remaining buffer content
        if (buffer && buffer.trim()) {
          console.log('Processing remaining buffer:', buffer);
          if (buffer.startsWith('data: ')) {
            const jsonStr = buffer.substring(6).trim();
            if (jsonStr !== '[DONE]') {
              try {
                const parsed = JSON.parse(jsonStr);
                // ... extract content as above
              } catch (e) {
                console.warn('Failed to parse final buffer:', e.message);
              }
            }
          }
        }

        const finalChunk = decoder.decode();
        if (finalChunk) {
          // Process final chunk similar to above
          accumulatedContent += finalChunk;
          if (onChunk) {
            onChunk(finalChunk, accumulatedContent);
          }
        }
      } catch (readError) {
        if (readError.name === 'AbortError' || readError.message.includes('aborted')) {
          console.log('Stream reading was aborted');
          throw new Error(`${provider} request was cancelled`);
        }
        throw readError;
      }
    }

    console.log(`✅ ${provider} streaming complete - ${accumulatedContent.length} chars, ${chunkCount} chunks`);

    // Return the accumulated content (even if empty, let the caller decide what to do)
    return accumulatedContent;

  } catch (error) {
    if (error.name === 'AbortError' || 
        (error.message && error.message.includes('was cancelled')) ||
        (error.message && error.message.includes('aborted'))) {
      console.log(`⏱️ ${provider} streaming request aborted`);
      throw new Error(`${provider} request was cancelled`);
    }
    console.error(`❌ Error in streaming call to ${provider}:`, error);
    throw error;
  } finally {
    // Cleanup
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (cleanupAbortListener) {
      cleanupAbortListener();
    }
    // Cancel reader if it exists
    if (reader) {
      reader.cancel().catch(() => {}); // Ignore errors
    }
  }
};


// Export current user ID helper
export const getCurrentUserId = () => {
  return auth.currentUser?.uid || null;
};